/**
 * RaceTrace Live: alerts for the viewer's cars on any tab (live-alerts.ts says when). Shown as a
 * stack of toasts, with an optional short tone and a vibration on a phone; each kind can be turned
 * off on this device. Off until the viewer turns them on from the one-time prompt or the bell.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { AnnotationData, RaceChartData } from "@shared/types";
import { Sheet } from "../../components/Sheet";
import { carNo } from "./car-number";
import { viewerCars } from "./RaceControl";
import type { MyCar } from "../my-cars/api";
import { liveStrategy, pitNowAll, type CautionRates, type PitWindow, type RaceHistory } from "./live-strategy";
import type { LiveField } from "./useLiveField";
import {
  ALERT_KINDS,
  ALERT_LABEL,
  alertsFor,
  emptyMemory,
  readAlertSettings,
  saveAlertSettings,
  type AlertMemory,
  type AlertSettings,
  type AlertTone,
  type LiveAlert,
} from "./live-alerts";

const C = {
  card: "#12122a",
  border: "#3a3a5c",
  text: "#e0e0e8",
  muted: "#9a9ab4",
  accent: "#F48C34",
};
const TONE: Record<AlertTone, string> = {
  info: "#22d3ee",
  warn: "#fab219",
  urgent: "#f87171",
};
/** A toast stays this long (ms); an urgent one longer. */
const SHOW_MS = 8_000;
const SHOW_URGENT_MS = 14_000;
/** Toasts shown at once. */
const MAX_TOASTS = 3;

/** The alert settings, kept on this device. */
export function useAlertSettings(): [AlertSettings, (s: AlertSettings) => void] {
  const [s, set] = useState<AlertSettings>(readAlertSettings);
  return [
    s,
    (next) => {
      set(next);
      saveAlertSettings(next);
    },
  ];
}

let audio: AudioContext | null = null;
/** Make sound possible: browsers allow it only after a tap, so this runs on the tap that turns it on. */
export function unlockSound(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!audio && Ctx) audio = new Ctx();
    void audio?.resume();
  } catch {
    audio = null;
  }
}

/** A short tone: two beeps for an urgent alert. */
function beep(tone: AlertTone): void {
  if (!audio || audio.state !== "running") return;
  const n = tone === "urgent" ? 2 : 1;
  for (let i = 0; i < n; i++) {
    const t = audio.currentTime + i * 0.22;
    const o = audio.createOscillator();
    const g = audio.createGain();
    o.frequency.value = tone === "info" ? 660 : 880;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.25, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    o.connect(g).connect(audio.destination);
    o.start(t);
    o.stop(t + 0.18);
  }
}

/**
 * The viewer's cars in this race (the focus car and their account's), and the alerts on them,
 * worked out each second from the live field while alerts are on.
 */
export function useLiveAlerts({
  data,
  annotations,
  field,
  focusNum,
  flag,
  timeToGoS,
  myCars,
  settings,
  cautionRates,
  history = null,
}: {
  data: RaceChartData | null;
  annotations: AnnotationData | null;
  field: LiveField;
  focusNum: number;
  flag: string | null;
  timeToGoS: number | null;
  myCars: MyCar[] | null;
  settings: AlertSettings;
  /** The race's series' caution rates (cautionRatesFor); WRL's when not given. */
  cautionRates?: CautionRates;
  /** What the series' earlier races say (GET /api/live/:slug/history); none until loaded. */
  history?: RaceHistory | null;
}): { cars: number[]; toasts: LiveAlert[]; dismiss: (id: string) => void } {
  const on = settings.on && !!data;
  const cars = useMemo(
    () => (data ? [...viewerCars(data, focusNum, new Set(), myCars).keys()] : []),
    [data, focusNum, myCars]
  );
  // The strategy for each car (its pit window), and where every car would rejoin: again when the
  // chart, the flag or the time to go (to 10 s) changes.
  const ttgStep = timeToGoS === null ? null : Math.round(timeToGoS / 10) * 10;
  const plan = useMemo(() => {
    if (!on || !data) return null;
    const window = new Map<number, PitWindow | null>();
    for (const k of cars)
      window.set(
        k,
        liveStrategy({ data, annotations, focusNum: k, flag, timeToGoS: ttgStep, cautionRates, history })?.focus
          ?.window ?? null
      );
    return { window, pitNow: pitNowAll(data, annotations, flag, ttgStep, cautionRates, history) };
  }, [on, data, annotations, flag, ttgStep, cars, cautionRates, history]);

  const memory = useRef<AlertMemory>(emptyMemory());
  const [toasts, setToasts] = useState<LiveAlert[]>([]);
  const kindsRef = useRef(settings);
  kindsRef.current = settings;
  // Turned off, or another race: start again from how things stand.
  const carsKey = cars.join(",");
  useEffect(() => {
    memory.current = emptyMemory();
  }, [on, carsKey]);

  useEffect(() => {
    if (!on || !data || !plan) return;
    const lap = new Map<number, number>();
    for (const k of cars) {
      const laps = data.cars[String(k)]?.laps ?? [];
      lap.set(k, (laps.length ? laps[laps.length - 1].l : 0) + 1);
    }
    const toKeys = (nums: string[]) =>
      new Set(
        nums
          .map((n) => Object.keys(data.cars).find((k) => carNo(data, Number(k)) === n))
          .filter((k): k is string => !!k)
          .map(Number)
      );
    const { alerts, memory: next } = alertsFor(memory.current, {
      now: field.now,
      flagChange: field.flagChange,
      cars,
      lap,
      closing: new Map(cars.map((k) => [k, field.closingOn(k)])),
      stopped: field.stopped,
      offTrack: toKeys(field.offTrack),
      inPit: new Set(field.pitVisits.keys()),
      window: plan.window,
      pitNow: plan.pitNow,
      name: (k) => `#${carNo(data, k)}`,
      cls: (k) => data.cars[String(k)]?.cls ?? "class",
    });
    memory.current = next;
    const shown = alerts.filter((a) => kindsRef.current.kinds[a.kind]);
    if (!shown.length) return;
    setToasts((prev) => [...shown, ...prev].slice(0, MAX_TOASTS * 3));
    const worst = shown.some((a) => a.tone === "urgent")
      ? "urgent"
      : shown.some((a) => a.tone === "warn")
        ? "warn"
        : "info";
    if (kindsRef.current.sound) beep(worst);
    if (kindsRef.current.vibrate)
      navigator.vibrate?.(worst === "urgent" ? [200, 100, 200] : 120);
  }, [field.now]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each toast goes after its time.
  useEffect(() => {
    if (!toasts.length) return;
    const t = setInterval(() => {
      const now = Date.now();
      setToasts((prev) =>
        prev.filter((a) => now - a.at < (a.tone === "urgent" ? SHOW_URGENT_MS : SHOW_MS))
      );
    }, 1_000);
    return () => clearInterval(t);
  }, [toasts.length]);

  return {
    cars,
    toasts: on ? toasts : [],
    dismiss: (id) => setToasts((prev) => prev.filter((a) => a.id !== id)),
  };
}

/** The alerts as they come: top right on a wide screen, at the bottom on a phone. Tap one to close it. */
export function AlertToasts({
  toasts,
  dismiss,
  compact,
}: {
  toasts: LiveAlert[];
  dismiss: (id: string) => void;
  compact: boolean;
}) {
  const shown = toasts.slice(0, MAX_TOASTS);
  return (
    <div
      className={`pointer-events-none fixed z-40 flex flex-col gap-2 ${compact ? "inset-x-3 bottom-3" : "right-4 top-24 w-80"}`}
      style={compact ? { paddingBottom: "env(safe-area-inset-bottom)" } : undefined}
      role="status"
      aria-live="polite"
      aria-label="Alerts"
    >
      {shown.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => dismiss(a.id)}
          className="pointer-events-auto flex min-h-[44px] items-center gap-2 rounded-md border px-3 py-2 text-left text-sm shadow-lg"
          style={{
            background: C.card,
            borderColor: C.border,
            borderLeft: `4px solid ${TONE[a.tone]}`,
            color: C.text,
          }}
          title="Tap to close"
        >
          <span className="min-w-0 flex-1">{a.text}</span>
          <span aria-hidden style={{ color: C.muted }}>
            ✕
          </span>
        </button>
      ))}
    </div>
  );
}

/** The bell: alerts on or off, and their settings. */
export function AlertsButton({
  settings,
  onOpen,
  compact,
}: {
  settings: AlertSettings;
  onOpen: () => void;
  compact: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Alerts ${settings.on ? "on" : "off"}: settings`}
      aria-haspopup="dialog"
      title={
        settings.on
          ? "Alerts on for your cars: tap for the settings"
          : "Alerts off: tap to turn them on"
      }
      className={
        compact
          ? "relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          : "ml-auto flex items-center gap-1.5 self-center rounded-md border px-2.5 py-1 text-xs"
      }
      style={{
        color: settings.on ? C.accent : C.muted,
        borderColor: settings.on ? C.accent : C.border,
      }}
    >
      <svg
        className={compact ? "h-6 w-6" : "h-4 w-4"}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z" />
        <path d="M10 20a2 2 0 0 0 4 0" />
        {!settings.on && <path d="M4 4l16 16" />}
      </svg>
      {!compact && (settings.on ? "Alerts on" : "Alerts off")}
      {/* On a phone, until the viewer has looked: a dot (the one-time prompt is the wide screen's). */}
      {compact && !settings.asked && !settings.on && (
        <span
          className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full"
          style={{ background: C.accent }}
          aria-hidden
        />
      )}
    </button>
  );
}

const carsText = (data: RaceChartData, cars: number[]) =>
  cars.map((k) => `#${carNo(data, k)}`).join(", ");

/** Once, on the live page: offer alerts for the viewer's cars. */
export function AlertsPrompt({
  data,
  cars,
  settings,
  onChange,
  compact,
}: {
  data: RaceChartData;
  cars: number[];
  settings: AlertSettings;
  onChange: (s: AlertSettings) => void;
  /** A phone: no prompt (the bell's dot instead), so the race views keep their place. */
  compact: boolean;
}) {
  // A phone has no room for it: the bell carries a dot until the viewer has looked (AlertsButton).
  if (compact || settings.asked || settings.on || !cars.length) return null;
  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-2 text-[13px] sm:px-6"
      style={{
        borderColor: "#1e1e38",
        background: "rgba(244,140,52,0.08)",
        color: C.text,
      }}
    >
      <span className="min-w-0 flex-1">
        Alerts for {carsText(data, cars)} on any tab: cautions, cars closing, stops, the
        pit window.
      </span>
      <button
        type="button"
        onClick={() => {
          unlockSound();
          onChange({ ...settings, on: true, asked: true });
        }}
        className="min-h-[44px] rounded-md px-3 text-[13px] font-semibold"
        style={{ background: C.accent, color: "#0b0b18" }}
      >
        Turn on
      </button>
      <button
        type="button"
        onClick={() => onChange({ ...settings, asked: true })}
        className="min-h-[44px] rounded-md px-2 text-[13px]"
        style={{ color: C.muted }}
      >
        Not now
      </button>
    </div>
  );
}

/** The alert settings: on or off, which kinds, sound and vibration (kept on this device). */
export function AlertsSheet({
  open,
  onClose,
  data,
  cars,
  settings,
  onChange,
}: {
  open: boolean;
  onClose: () => void;
  data: RaceChartData;
  cars: number[];
  settings: AlertSettings;
  onChange: (s: AlertSettings) => void;
}) {
  const row = "flex min-h-[44px] items-center gap-3 text-[15px]";
  const box = "h-5 w-5 shrink-0 accent-[#F48C34]";
  return (
    <Sheet open={open} onClose={onClose} title="Alerts">
      <div className="flex flex-col gap-1" style={{ color: C.text }}>
        <label
          className={row}
          title="Alerts for these cars on any tab of this page; off until turned on, kept on this device"
        >
          <input
            type="checkbox"
            className={box}
            checked={settings.on}
            onChange={(e) => {
              if (e.target.checked) unlockSound();
              onChange({ ...settings, on: e.target.checked, asked: true });
            }}
          />
          <span>
            Alerts for {cars.length ? carsText(data, cars) : "the focus car"}
            <span className="block text-[13px]" style={{ color: C.muted }}>
              The focus car and the cars on your account, on any tab while this page is
              open.
            </span>
          </span>
        </label>
        <div
          className="mt-2 text-[12px] font-semibold uppercase tracking-wider"
          style={{ color: C.muted }}
        >
          When
        </div>
        {ALERT_KINDS.map((k) => (
          <label
            key={k}
            className={row}
            style={{ opacity: settings.on ? 1 : 0.5 }}
            title={`Alert when: ${ALERT_LABEL[k].toLowerCase()}`}
          >
            <input
              type="checkbox"
              className={box}
              disabled={!settings.on}
              checked={settings.kinds[k]}
              onChange={(e) =>
                onChange({
                  ...settings,
                  kinds: { ...settings.kinds, [k]: e.target.checked },
                })
              }
            />
            {ALERT_LABEL[k]}
          </label>
        ))}
        <div
          className="mt-2 text-[12px] font-semibold uppercase tracking-wider"
          style={{ color: C.muted }}
        >
          How
        </div>
        <label
          className={row}
          style={{ opacity: settings.on ? 1 : 0.5 }}
          title="A short beep with each alert (two for an urgent one)"
        >
          <input
            type="checkbox"
            className={box}
            disabled={!settings.on}
            checked={settings.sound}
            onChange={(e) => {
              if (e.target.checked) unlockSound();
              onChange({ ...settings, sound: e.target.checked });
            }}
          />
          A short tone
        </label>
        <label
          className={row}
          style={{ opacity: settings.on ? 1 : 0.5 }}
          title="A buzz with each alert, on phones that can"
        >
          <input
            type="checkbox"
            className={box}
            disabled={!settings.on}
            checked={settings.vibrate}
            onChange={(e) => onChange({ ...settings, vibrate: e.target.checked })}
          />
          Vibrate (phones that can)
        </label>
        <p className="mt-2 text-[13px]" style={{ color: C.muted }}>
          Alerts come while this page is open, on any tab. Estimates from the timing:
          closing and stops use the cars&apos; positions, which can lag a few seconds.
        </p>
      </div>
    </Sheet>
  );
}
